import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, History, TriangleAlert, Wrench } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  DesignChange,
  FailureMode,
  FailureModeRevisionPreview,
  SessionResultsResponse,
  UpdateDesignChangeReviewRequest,
  UpdateFailureModeReviewRequest,
} from '../../../../types';
import DRBFMTable from '../../DRBFMTable';
import DRBFMDecisionMemoryPanel from '../../DRBFMDecisionMemoryPanel';

interface ResultsTabsProps {
  results: SessionResultsResponse;
  tabValue: number;
  onTabChange: (value: number) => void;
  drbfmSessionId?: string;
  selectedFailureMode?: FailureMode;
  selectedDesignChange?: DesignChange;
  onSelectFailureMode: (failureMode: FailureMode) => void;
  isDecisionPanelOpen: boolean;
  onCloseDecisionPanel: () => void;
  onOpenDrawingEvidence: (failureMode: FailureMode) => void;
  reviewingArtifactId?: string;
  excludedCount?: number;
  historyMode?: boolean;
  onToggleHistory?: () => void;
  onReviewDesignChange?: (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => Promise<void>;
  onReviewFailureMode?: (
    failureModeId: string,
    request: UpdateFailureModeReviewRequest,
  ) => Promise<void>;
  onPreviewFailureModeRevision?: (
    failureModeId: string,
    instruction: string,
  ) => Promise<FailureModeRevisionPreview>;
}

const ResultsTabs: React.FC<ResultsTabsProps> = ({
  results,
  tabValue,
  onTabChange,
  drbfmSessionId,
  selectedFailureMode,
  selectedDesignChange,
  onSelectFailureMode,
  isDecisionPanelOpen,
  onCloseDecisionPanel,
  onOpenDrawingEvidence,
  reviewingArtifactId,
  excludedCount = 0,
  historyMode = false,
  onToggleHistory,
  onReviewDesignChange,
  onReviewFailureMode,
  onPreviewFailureModeRevision,
}) => {
  const { t } = useTranslation();
  const designChangesCount = results.designChanges?.length || 0;
  const failureModesCount = results.failureModes?.length || 0;
  const countermeasuresCount = results.countermeasures?.length || 0;
  const canReviewSelectedFailureMode = Boolean(
    selectedFailureMode
    && onReviewFailureMode
    && selectedFailureMode.reviewStatus !== 'superseded'
    && (!historyMode || selectedFailureMode.reviewStatus === 'rejected'),
  );

  return (
    <Card>
      <Tabs
        value={String(tabValue)}
        onValueChange={(value) => onTabChange(Number(value))}
      >
        <div className="border-b border-border">
          <TabsList
            aria-label={t('drbfm.results.tabsAria')}
            className="h-auto justify-start rounded-none bg-transparent p-0"
          >
            <TabsTrigger value="0" className="gap-2 rounded-none">
              <ArrowLeftRight className="h-4 w-4" />
              {`${t('drbfm.table.designChanges')} (${designChangesCount})`}
            </TabsTrigger>
            <TabsTrigger value="1" className="gap-2 rounded-none">
              <TriangleAlert className="h-4 w-4" />
              {`${t('drbfm.table.failureModes')} (${failureModesCount})`}
            </TabsTrigger>
            <TabsTrigger value="2" className="gap-2 rounded-none">
              <Wrench className="h-4 w-4" />
              {`${t('drbfm.table.countermeasures')} (${countermeasuresCount})`}
            </TabsTrigger>
          </TabsList>
        </div>

        {excludedCount > 0 && (
          <div className="flex flex-col gap-2 px-6 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {historyMode && (
                <p className="revy-cjk text-sm font-medium">
                  {t('drbfm.decisionMemory.historyTitle', '判断履歴')}
                </p>
              )}
              <p className="revy-cjk text-xs leading-[1.6] text-muted-foreground">
                {historyMode
                  ? t(
                    'drbfm.decisionMemory.historyDescription',
                    '非採用・旧版として除外した内容です。非採用の変更点は再検討に戻し、故障モードは再検討して採用できます。',
                  )
                  : t('drbfm.decisionMemory.excludedHistory', {
                    count: excludedCount,
                    defaultValue: `非採用・旧版 ${excludedCount}件は一覧から除外し、判断履歴として保持しています。`,
                  })}
              </p>
            </div>
            {onToggleHistory && (
              <Button size="sm" variant="outline" onClick={onToggleHistory}>
                <History />
                {historyMode
                  ? t('drbfm.decisionMemory.showActiveResults', '現在の結果に戻る')
                  : t('drbfm.decisionMemory.showExcludedHistory', '非採用・旧版を表示')}
              </Button>
            )}
          </div>
        )}

        <CardContent>
          <TabsContent value="0" className="pt-2">
            <DRBFMTable
              type="designChanges"
              data={results.designChanges || []}
              reviewingArtifactId={reviewingArtifactId}
              onReviewDesignChange={onReviewDesignChange}
            />
          </TabsContent>
          <TabsContent value="1" className="pt-2">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
              <div
                className={cn(
                  'col-span-1',
                  isDecisionPanelOpen ? 'lg:col-span-8' : 'lg:col-span-12',
                )}
              >
                <DRBFMTable
                  type="failureModes"
                  data={results.failureModes || []}
                  drbfmSessionId={drbfmSessionId}
                  selectedFailureModeId={selectedFailureMode?.id}
                  onSelectFailureMode={onSelectFailureMode}
                />
              </div>
              {isDecisionPanelOpen && (
                <div className="col-span-1 lg:col-span-4">
                  <DRBFMDecisionMemoryPanel
                    failureMode={selectedFailureMode || null}
                    designChange={selectedDesignChange}
                    isReviewing={Boolean(reviewingArtifactId)}
                    onPreviewRevision={selectedFailureMode && onPreviewFailureModeRevision
                      ? (instruction) => onPreviewFailureModeRevision(
                        selectedFailureMode.id,
                        instruction,
                      )
                      : undefined}
                    onReview={canReviewSelectedFailureMode
                      && selectedFailureMode
                      && onReviewFailureMode
                      ? (request) => onReviewFailureMode(selectedFailureMode.id, request)
                      : undefined}
                    onOpenDrawingEvidence={onOpenDrawingEvidence}
                    onClose={onCloseDecisionPanel}
                  />
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="2" className="pt-2">
            <DRBFMTable
              type="countermeasures"
              data={results.countermeasures || []}
            />
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
};

export default ResultsTabs;
